import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import { RecruitmentService } from '../../service/RecruitmentService';
import GeneralUtils from '../../utilities/GeneralUtils';
import { Dropdown } from 'primereact/dropdown';
import DataTableUtils from '../../utilities/DataTableUtils';
import RecruitmentUtils from '../../utilities/RecruitmentUtils';

const RecruitmentPlanJobSecretEdit = (props) => {
    let emptyJob = {
        id: null,
        recruitmentPlan: { id: null, name: '' },
        type: null,
        institutionJob: { id: null },
        departmentType: '',
        departmentName: '',
        position: '',
        positionClass: '',
        positionLevel: '',
        jobID: '',
        jobList: [],
        vacancyForecasted: false,
        vacancyYear: GeneralUtils.getCurrentYear(),
        vacancySemester: 1
    };

    const [job, setJob] = useState(null);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isNew, setIsNew] = useState(true);

    const recruitmentService = useMemo(() => new RecruitmentService(), []);

    const toast = useRef(null);

    useEffect(() => {
        let _job = { ...emptyJob, recruitmentPlan: props?.recruitmentPlan, type: props?.type };
        if (props?.value) {
            _job = GeneralUtils.emptyInNullForObjectFields(props?.value);

            if (!props?.recruitmentPlan?.institution?.secret) {
                _job = RecruitmentUtils.institutionJobToRecruitmentPlanJob(_job.institutionJob, _job)
            }

            setIsNew(false);
        }

        setJob(_job);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, recruitmentService]);

    const hideDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        formik.resetForm();
    };

    const saveDetails = (data) => {
        recruitmentService.saveRecruitmentPlanJob(data, true).then(
            () => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(null, isNew);
                }
                hideDialog()
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: job ? job : emptyJob,
        validate: (data) => {
            let errors = {};

            if (props?.type !== RecruitmentUtils.getRecruitmentPlanJobType().INALTI_FUNCTIONARI_PUBLICI) {
                if (!data.departmentType) {
                    errors.departmentType = translatedMessage("form.error.recruitmentPlanJob.departmentType.required");
                }
                if (!data.departmentName) {
                    errors.departmentName = translatedMessage("form.error.recruitmentPlanJob.departmentName.required");
                }
                if (!data.positionClass) {
                    errors.positionClass = translatedMessage("form.error.recruitmentPlanJob.positionClass.required");
                }
                if (!data.positionLevel) {
                    errors.positionLevel = translatedMessage("form.error.recruitmentPlanJob.positionLevel.required");
                }
            }

            if (!data.position) {
                errors.position = translatedMessage("form.error.recruitmentPlanJob.position.required");
            }

            if (data.vacancyForecasted && (!data.vacancyYear || parseInt(data.vacancyYear) === 0)) {
                errors.vacancyYear = translatedMessage("form.error.recruitmentPlanJob.vacancyYear.required");
            }

            if (data.vacancyForecasted && (!data.vacancySemester)) {
                errors.vacancySemester = translatedMessage("form.error.recruitmentPlanJob.vacancySemester.required");
            }

            return errors;
        },
        onSubmit: (data) => {
            saveDetails(data);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const dialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-check" className="p-button p-button-primary" form="job-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />

            <Dialog
                visible={props?.visible}
                header={(job?.id ? translatedMessage('job.edit') : translatedMessage('job.add')) + " - " + translatedMessage('RecruitmentPlanJobType.' + props?.type)}
                modal
                resizable={false}
                className="p-fluid"
                style={{ width: '650px' }}
                footer={dialogFooter}
                onHide={hideDialog}
            >
                <form id="job-form" onSubmit={formik.handleSubmit}>
                    <div className="grid">
                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <InputText type="text" name="departmentType" id="departmentType"
                                            value={formik.values.departmentType} onChange={formik.handleChange}
                                            className={classNames({ 'p-invalid': isFormFieldValid('departmentType') })} autoFocus={true}
                                        />
                                        <label htmlFor="departmentType" className={classNames({ 'p-error': isFormFieldValid('departmentType') })}>
                                            {translatedMessage("job.departmentType")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('departmentType')}</div>
                            </div>
                        }

                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <InputText type="text" name="departmentName" id="departmentName"
                                            value={formik.values.departmentName} onChange={formik.handleChange}
                                            className={classNames({ 'p-invalid': isFormFieldValid('departmentName') })}
                                        />
                                        <label htmlFor="departmentName" className={classNames({ 'p-error': isFormFieldValid('departmentName') })}>
                                            {translatedMessage("job.departmentName")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('departmentName')}</div>
                            </div>
                        }

                        <div className="p-inputgroup col-12 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <InputText type="text" name="position" id="position"
                                        value={formik.values.position} onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('position') })}
                                    />
                                    <label htmlFor="position" className={classNames({ 'p-error': isFormFieldValid('position') })}>
                                        {translatedMessage("job.position")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('position')}</div>
                        </div>

                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 sm:col-6 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <InputText type="text" name="positionClass" id="positionClass"
                                            value={formik.values.positionClass} onChange={formik.handleChange}
                                            className={classNames({ 'p-invalid': isFormFieldValid('positionClass') })}
                                        />
                                        <label htmlFor="positionClass" className={classNames({ 'p-error': isFormFieldValid('positionClass') })}>
                                            {translatedMessage("job.positionClass")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('positionClass')}</div>
                            </div>
                        }

                        {!RecruitmentUtils.hideField(props?.type) &&
                            <div className="p-inputgroup col-12 sm:col-6 flex-column">
                                <div className='col-12 p-0'>
                                    <span className="p-float-label">
                                        <InputText type="text" name="positionLevel" id="positionLevel"
                                            value={formik.values.positionLevel} onChange={formik.handleChange}
                                            className={classNames({ 'p-invalid': isFormFieldValid('positionLevel') })}
                                        />
                                        <label htmlFor="positionLevel" className={classNames({ 'p-error': isFormFieldValid('positionLevel') })}>
                                            {translatedMessage("job.positionLevel")} *
                                        </label>
                                    </span>
                                </div>
                                <div className="p-0 col-12">{getFormErrorMessage('positionLevel')}</div>
                            </div>
                        }
                        <div className="p-inputgroup col-12 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <InputText type="text" name="jobID" id="jobID"
                                        value={formik.values.jobID} onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('jobID') })}
                                    />
                                    <label htmlFor="jobID" className={classNames({ 'p-error': isFormFieldValid('jobID') })}>
                                        {translatedMessage("job.jobID")}
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('jobID')}</div>
                        </div>

                        <div className="p-inputgroup col-12 sm:col-3">
                            {translatedMessage("job.vacancyForecasted")}
                        </div>
                        <div className="p-inputgroup col-12 sm:col-3 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <Dropdown
                                        value={formik.values.vacancyForecasted}
                                        options={RecruitmentUtils.yesNoDropdownOptions()}
                                        onChange={(e) => formik.setFieldValue("vacancyForecasted", e.value)}
                                        className="p-column-filter"
                                        itemTemplate={(e) => DataTableUtils.yesNoTemplate(e.value)}
                                    />

                                    <label htmlFor="vacancyForecasted" className={classNames({ 'p-error': isFormFieldValid('vacancyForecasted') })}>
                                        {translatedMessage("generic.yesNo")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('vacancyForecasted')}</div>
                        </div>

                        <div className="p-inputgroup col-12 sm:col-3 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <InputText type="text" name="vacancyYear" id="vacancyYear"
                                        value={formik.values.vacancyYear} onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('vacancyYear') })}
                                        keyfilter="int" maxLength={4} disabled={!formik.values.vacancyForecasted} />
                                    <label htmlFor="vacancyYear" className={classNames({ 'p-error': isFormFieldValid('vacancyYear') })}>
                                        {translatedMessage("job.vacancyYear")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('vacancyYear')}</div>
                        </div>

                        <div className="p-inputgroup col-12 sm:col-3 flex-column">
                            <div className='col-12 p-0'>
                                <span className="p-float-label">
                                    <Dropdown
                                        value={formik.values.vacancySemester}
                                        options={RecruitmentUtils.semesterDropdownOptions()}
                                        onChange={(e) => formik.setFieldValue("vacancySemester", e.value)}
                                        className="p-column-filter"
                                        itemTemplate={(e) => translatedMessage("semester." + e.value)}
                                        disabled={!formik.values.vacancyForecasted}
                                    />

                                    <label htmlFor="vacancySemester" className={classNames({ 'p-error': isFormFieldValid('vacancySemester') })}>
                                        {translatedMessage("job.vacancySemester")} *
                                    </label>
                                </span>
                            </div>
                            <div className="p-0 col-12">{getFormErrorMessage('vacancySemester')}</div>
                        </div>

                    </div>
                </form>
            </Dialog>

            {showValidationErrorDialog &&
                <ValidationErrorModalComponent
                    visible={showValidationErrorDialog}
                    errors={validationErrors}
                    dialogTitle={translatedMessage("recruitment.plan.save.error.title")}
                    closeDialog={() => setShowValidationErrorDialog(false)}
                />
            }
        </>
    );
};

export default RecruitmentPlanJobSecretEdit;
